import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowSquareOut,
  TrendUp,
  Brain,
  Lock,
  CaretDown,
  CaretUp,
  Star,
  Target,
  Rocket,
  CalendarBlank,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { tradingViewStockUrl, screenerStockUrl } from "../../lib/tradingview";
import StockList from "./StockList";

const fmt = (n, d = 2) =>
  typeof n === "number"
    ? n.toLocaleString("en-IN", { maximumFractionDigits: d, minimumFractionDigits: d })
    : "—";

const ratingColor = {
  Strong: "text-pos border-pos bg-pos-soft",
  Moderate: "text-mod border-mod bg-mod-soft",
  Weak: "text-neg border-neg bg-neg-soft",
};

const FLAVOUR_LABEL = {
  short: { label: "Short-Term", desc: "Near 6-month rim · ideal for swing entries" },
  long: { label: "Long-Term", desc: "Near 3-year rim · structural breakout coming" },
};

function ApproachingCard({ item, onWatch, watched }) {
  const [open, setOpen] = useState(false);
  const sym = (item.symbol || "").replace(".NS", "");
  const flav = FLAVOUR_LABEL[item.flavour] || FLAVOUR_LABEL.short;
  const fundamentals =
    Boolean(item.fundamental_summary) ||
    (item.fundamental_bullish?.length > 0) ||
    (item.fundamental_bearish?.length > 0);
  const gapPct = typeof item.gap_pct === "number" ? item.gap_pct : 0;
  const proximityHeat =
    gapPct <= 1 ? "text-pos" : gapPct <= 2.5 ? "text-mod" : "text-secondary-fg";

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="border border-subtle bg-surface rounded-sm p-5 hover:border-pos/40 transition"
      data-testid={`approaching-card-${sym}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-0.5 flex-wrap">
            <a
              href={tradingViewStockUrl(item.symbol)}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-lg font-bold text-primary-fg hover:text-pos flex items-center gap-1"
              data-testid={`approaching-tv-${sym}`}
            >
              {sym} <ArrowSquareOut size={12} weight="bold" />
            </a>
            <span
              className={
                "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
                (item.flavour === "long"
                  ? "text-pos border-pos bg-pos-soft"
                  : "text-mod border-mod bg-mod-soft")
              }
              data-testid={`approaching-flavour-${sym}`}
              title={flav.desc}
            >
              {flav.label}
            </span>
            {item.fundamental_rating && (
              <span
                className={
                  "px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded-sm " +
                  (ratingColor[item.fundamental_rating] || "text-muted-fg border-subtle")
                }
                data-testid={`approaching-fundamental-${sym}`}
                title={`Fundamental: ${item.fundamental_rating}${
                  item.fundamental_score ? " (" + item.fundamental_score + "/10)" : ""
                }`}
              >
                <Brain size={9} weight="fill" className="inline mr-0.5" />
                {item.fundamental_rating}
                {item.fundamental_score ? ` ${item.fundamental_score}/10` : ""}
              </span>
            )}
          </div>
          <div className="text-sm text-muted-fg truncate">
            {item.company_name || sym}
          </div>
        </div>
        <div className="flex items-start gap-2 shrink-0">
          {onWatch && (
            <button
              type="button"
              onClick={() => onWatch({ symbol: item.symbol })}
              data-testid={`approaching-watch-${sym}`}
              title={watched ? "Remove from watchlist" : "Add to watchlist"}
              className={
                "p-1.5 rounded-sm border transition " +
                (watched
                  ? "bg-pos-soft border-pos text-pos hover:bg-pos-soft"
                  : "border-subtle text-muted-fg hover:text-pos hover:border-pos/40")
              }
            >
              <Star size={13} weight={watched ? "fill" : "regular"} />
            </button>
          )}
          <div className="text-right">
            <div className="text-[9px] font-mono uppercase tracking-widest text-muted-fg">
              Current
            </div>
            <div className="font-mono text-lg text-primary-fg tabular-nums">
              ₹{fmt(item.last_price)}
            </div>
            {typeof item.pct_change_today === "number" && (
              <div
                className={
                  "text-[10px] font-mono tabular-nums " +
                  (item.pct_change_today >= 0 ? "text-pos" : "text-neg")
                }
                title={
                  item.last_price_refreshed_at
                    ? `Today's change · refreshed ${new Date(item.last_price_refreshed_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}`
                    : "Today's change vs prev close"
                }
              >
                {item.pct_change_today >= 0 ? "+" : ""}
                {item.pct_change_today.toFixed(2)}% today
              </div>
            )}
            <div className={"text-[10px] font-mono tabular-nums " + proximityHeat}>
              {fmt(gapPct)}% below rim
            </div>
          </div>
        </div>
      </div>

      {/* Proximity bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          <span className="flex items-center gap-1">
            <Rocket size={10} weight="fill" className="text-pos" /> Proximity to breakout
          </span>
          <span className="text-secondary-fg tabular-nums">
            {fmt(gapPct)}% gap · rim ₹{fmt(item.rim)}
          </span>
        </div>
        <div className="h-1.5 bg-app border border-subtle rounded-sm overflow-hidden">
          <div
            className="h-full bg-pos transition-all"
            style={{ width: `${Math.max(0, Math.min(100, (1 - gapPct / 5) * 100))}%` }}
            title={`${fmt(gapPct)}% below rim (closer to 0 = closer to breakout)`}
          />
        </div>
      </div>

      {/* Setup strip: rim / vol / trend */}
      <div className="flex items-center justify-between mb-3 px-3 py-2 bg-app border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-muted-fg">
        <div className="flex items-center gap-1.5" title="Breakout rim level">
          <Target size={10} weight="bold" className="text-pos" />
          <span className="text-secondary-fg tabular-nums">₹{fmt(item.rim)}</span> rim
        </div>
        <div className="flex items-center gap-1.5" title="Recent 20d vs prior 20d volume">
          <TrendUp size={10} weight="bold" className="text-pos" />
          <span className="text-secondary-fg tabular-nums">
            {fmt(item.volume_accumulation, 1)}×
          </span>{" "}
          vol accum
        </div>
        <div className="flex items-center gap-1.5" title="Rim was last hit on this date">
          <CalendarBlank size={10} weight="bold" />
          <span className="text-secondary-fg tabular-nums">{item.rim_date || "—"}</span>
        </div>
      </div>

      {/* Deep links */}
      <div className="flex items-center gap-2 mb-1">
        <a
          href={tradingViewStockUrl(item.symbol)}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-2 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition"
          data-testid={`approaching-tv-btn-${sym}`}
        >
          TV ↗
        </a>
        <a
          href={screenerStockUrl(item.symbol)}
          target="_blank"
          rel="noreferrer"
          className="flex-1 text-center px-2 py-1.5 border border-subtle rounded-sm text-[10px] font-mono uppercase tracking-widest text-secondary-fg hover:text-primary-fg hover:border-pos/40 transition"
          data-testid={`approaching-scr-${sym}`}
        >
          SCR ↗
        </a>
      </div>

      {/* Fundamentals expandable */}
      {fundamentals && (
        <div className="mt-3 pt-3 border-t border-subtle">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`approaching-fund-toggle-${sym}`}
            className="w-full flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition"
          >
            <span className="flex items-center gap-1.5">
              <Brain size={11} weight="fill" className="text-pos" /> Fundamentals (AI)
            </span>
            {open ? <CaretUp size={11} /> : <CaretDown size={11} />}
          </button>
          {open && (
            <div className="mt-2.5 space-y-2 text-xs text-secondary-fg leading-relaxed">
              {item.fundamental_summary && (
                <p className="text-primary-fg">{item.fundamental_summary}</p>
              )}
              {item.fundamental_bullish?.length > 0 && (
                <div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-pos mb-1">
                    Bullish
                  </div>
                  <ul className="space-y-0.5">
                    {item.fundamental_bullish.map((pt) => (
                      <li key={`bull-${pt}`} className="flex gap-1.5">
                        <span className="text-pos">+</span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.fundamental_bearish?.length > 0 && (
                <div>
                  <div className="text-[9px] font-mono uppercase tracking-widest text-neg mb-1">
                    Bearish
                  </div>
                  <ul className="space-y-0.5">
                    {item.fundamental_bearish.map((pt) => (
                      <li key={`bear-${pt}`} className="flex gap-1.5">
                        <span className="text-neg">−</span>
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}

export default function ApproachingView({ onWatch, watchedSet, onOpenStock }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // 'all' | 'short' | 'long'

  useEffect(() => {
    setLoading(true);
    api
      .get("/approaching")
      .then(({ data }) => setData(data))
      .catch((e) => {
        console.error("/approaching load", e);
        setData({ items: [], is_premium_view: true });
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="text-center text-muted-fg py-16" data-testid="approaching-loading">
        Loading approaching breakouts…
      </div>
    );
  }

  const allItems = data?.items || [];
  const items = filter === "all" ? allItems : allItems.filter((it) => it.flavour === filter);
  const isPremium = data?.is_premium_view;
  const shortCount = allItems.filter((it) => it.flavour === "short").length;
  const longCount = allItems.filter((it) => it.flavour === "long").length;

  return (
    <div data-testid="approaching-view">
      <div className="mb-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Rocket size={14} weight="fill" className="text-pos" />
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-primary-fg">
              Approaching Breakouts
            </div>
            <span className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest border border-pos/40 text-pos bg-pos-soft rounded-sm">
              Within 5% of rim · trending + accumulating
            </span>
          </div>
          <p className="text-xs text-muted-fg max-w-2xl leading-relaxed">
            Top 10 stocks coiled just below their 6-month (short) or 3-year (long)
            rim — price &gt; EMA50 &gt; EMA200, volume accumulating, and
            fundamentals favour a strong breakout. Get ready <b>before</b> they
            pop, not after. Refreshes every 30 min during NSE hours.
          </p>
        </div>
        <div
          className="inline-flex border border-subtle rounded-sm p-0.5 bg-app shrink-0"
          data-testid="approaching-filter"
        >
          {[
            { k: "all", label: `All · ${allItems.length}` },
            { k: "short", label: `Short · ${shortCount}` },
            { k: "long", label: `Long · ${longCount}` },
          ].map((f) => (
            <button
              key={f.k}
              type="button"
              onClick={() => setFilter(f.k)}
              data-testid={`approaching-filter-${f.k}`}
              className={
                "px-2.5 py-1 text-[10px] font-mono uppercase tracking-widest rounded-sm transition " +
                (filter === f.k
                  ? "bg-pos-soft text-pos"
                  : "text-muted-fg hover:text-primary-fg")
              }
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div
          className="p-10 border border-subtle rounded-sm bg-surface text-center"
          data-testid="approaching-empty"
        >
          <Rocket size={24} className="mx-auto text-muted-fg mb-3" />
          <div className="text-primary-fg font-medium mb-1">
            {allItems.length === 0
              ? "No approaching breakouts qualifying right now"
              : `No ${filter} approaching breakouts`}
          </div>
          <div className="text-sm text-muted-fg max-w-md mx-auto">
            {allItems.length === 0 ? (
              <>
                We need the stock within 5% of its rim, with price above EMA50
                above EMA200, and recent volume 1.2× the prior 20-day average.
                Scan refreshes every 30 min during NSE market hours.
              </>
            ) : (
              <>
                Try the <b>All</b> tab to see every approaching setup.
              </>
            )}
          </div>
        </div>
      ) : (
        <>
          <StockList
            items={items}
            onOpen={(it) => onOpenStock?.({ ...it, approaching_mode: true })}
            onWatch={onWatch}
            watchedSet={watchedSet}
            testIdPrefix="approaching-row"
            testid="approaching-list"
            rowProps={(it) => ({
              iconKind: "approaching",
              typeBadge:
                it.flavour === "long"
                  ? { label: "Long", tone: "pos" }
                  : { label: "Short", tone: "mod" },
              rightHint:
                typeof it.gap_pct === "number"
                  ? `${it.gap_pct.toFixed(2)}% to rim`
                  : undefined,
            })}
          />
          {!isPremium && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-6 border border-pos/40 rounded-sm p-5 bg-pos-soft/40 flex items-center justify-between gap-4 flex-wrap"
              data-testid="approaching-upgrade"
            >
              <div className="flex items-center gap-3">
                <Lock size={20} className="text-pos shrink-0" />
                <div>
                  <div className="font-medium text-primary-fg">
                    Showing 1 short + 1 long preview
                  </div>
                  <div className="text-sm text-secondary-fg">
                    Unlock all 10 approaching breakouts with a ₹5 trial.
                  </div>
                </div>
              </div>
              <Link
                to="/pricing"
                className="px-5 py-2.5 rounded-sm text-sm font-medium"
                style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
              >
                Start ₹5 trial →
              </Link>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}
